import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthClientConfig } from '@auth0/auth0-angular';
import { FeatureToggleResponse } from 'app/services/global/feature-manager/feature-manager.service';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiVersionEnum } from '../enums/global/api-versions.enum';
import { Configuration } from './config-model';
import { IConfigService } from './iconfigservice';

@Injectable()
export class ConfigService implements IConfigService {
    private config: Configuration;
    private whiteLabelConfiguration: any;
    private featureFlags: Array<FeatureToggleResponse>;
    authClientConfig: AuthClientConfig;
    handler: HttpBackend;

    public loadingData: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(private http: HttpClient, handler: HttpBackend, authClientConfig: AuthClientConfig) {
        this.authClientConfig = authClientConfig;
        this.handler = handler;
    }

    async load(url?: string) {
        if (this.config !== undefined) {
            return new Promise((resolve, reject) => {
                resolve(this.config);
            });
        }

        this.loadingData.next(true);
        const httpClient = new HttpClient(this.handler);

        if (url === undefined) {
            const bootstrap = await lastValueFrom(
                httpClient.get<{ apiUrl: string }>(environment.configFile)
            );
            const apiBase = bootstrap.apiUrl.replace('/api/v1/', '');
            url = `${apiBase}/api/client-config`;
        }

        return lastValueFrom(httpClient.get(url))
            .then(async (config: Configuration) => {
                this.validateConfig(config);
                this.config = config;
                this.featureFlags = config.featureFlags ?? [];

                const port = window.location.port;

                if (port !== '') {
                    this.config.auth0Config.redirectUri = `${window.location.protocol}//${window.location.hostname}:${port}`;
                } else {
                    this.config.auth0Config.redirectUri = `${window.location.protocol}//${window.location.hostname}`;
                }

                this.authClientConfig.set({
                    domain: this.config.auth0V2Config.domain,
                    clientId: this.config.auth0V2Config.clientId,
                    authorizationParams: {
                        redirectUri: window.location.origin + '/authorize',
                        audience: this.config.auth0V2Config.audience,
                    },
                    cacheLocation: 'localstorage',
                    httpInterceptor: {
                        allowedList: [
                            {
                                uri: this.config.apiUrl.replace('v1/', '*'),
                                allowAnonymous: true
                            },
                            {
                                uri: this.config.publicApiUrl.replace('v2/', '*'),
                                allowAnonymous: true
                            },
                            {
                                uri: this.config.whiteLabelResourceUrl,
                                allowAnonymous: true
                            },
                            {
                                uri:this.config.underwritingApiUrl + '/*',
                                allowAnonymous: true
                            },
                            {
                                uri:this.config.mrasApiUrl + '/*',
                                allowAnonymous: true
                            }
                        ]
                    }
                });

                await this.loadThemes(config);
                return config;
            });
    }

    private validateConfig(config: Configuration) {
        const requiredKeys: Array<keyof Configuration> = [
            'apiUrl',
            'publicApiUrl',
            'signalRBaseUrl',
            'sdkApiUrl',
            'mrasApiUrl',
            'webUrl',
            'whiteLabelResourceUrl',
            'auth0Config',
            'auth0V2Config',
            'appInsights'
        ];

        const missing = requiredKeys.filter((key) => !config?.[key]);

        if (missing.length > 0) {
            throw new Error(`Client config missing required keys: ${missing.join(', ')}`);
        }
    }

    getConfiguration(): Configuration {
        return this.config;
    }

    public async loadThemes(config: Configuration) {
        this.whiteLabelConfiguration = {
            themeDefaults: config.whiteLabelConfigurations?.defaultConfiguration,
            subdomainThemes: config.whiteLabelConfigurations?.clientConfigurations,
            themesLoaded: true,
            whiteLabelResourceUrl: this.config.whiteLabelResourceUrl
        };
        this.loadingData.next(false);
    }

    getPublicApiUrl(apiVersion: ApiVersionEnum): string {
        if (apiVersion !== ApiVersionEnum.v2) {
            return this.config.publicApiUrl.replace('v2', apiVersion);
        }
        return this.config.publicApiUrl;
    }

    
    getMrasApiUrl(_version?: string): string {
        return this.config.mrasApiUrl;
    }

    getWhiteLabelConfiguration(){
        return this.whiteLabelConfiguration;
    }

    getFeatureFlags(){
        return this.featureFlags;
    }

    getUnderwriterApiUrl(): string {
        return this.config.underwritingApiUrl;
    }

    getAllowedLogoutRoutes(): string {
        return this.config.allowedLogoutRoutes;
    }

    getExcludedQueryParams(): string[] {
        return this.config.excludedQueryParams.split(',');
    }
}
